import type { ScoreInput } from '../index.js'

const shifts = (count: number): string => (count === 1 ? 'shift' : 'shifts')

/**
 * A behavior's count in a whole phrase that says what was counted. The
 * engine measures call-outs and no-shows over the shifts scheduled, but
 * punctuality and completion over the shifts actually worked (scheduled
 * minus call-outs and no-shows: a called-out shift is not also late or
 * unfinished, the call-out already counts). The short form, "2 of 4
 * worked", read as "worked 2 of 4" and made a poor month look fine, so
 * each phrase names the thing counted.
 */
const COUNTED_PHRASES: Record<string, (count: number, of: number) => string> = {
  call_out_rate: (count, of) => `${count} ${count === 1 ? 'call-out' : 'call-outs'} in ${of} scheduled ${shifts(of)}`,
  no_show_rate: (count, of) => `${count} ${count === 1 ? 'no-show' : 'no-shows'} in ${of} scheduled ${shifts(of)}`,
  on_time_arrival: (count, of) => `On time for ${count} of ${of} ${shifts(of)} worked`,
  shift_completion: (count, of) => `Completed ${count} of ${of} ${shifts(of)} worked`,
  // One shift, one chart: the count is of the charts that were due.
  charting_completion: (count, of) => `${count} of ${of} charts`,
}

const PERCENT_OF = 100

function isCounted(input: ScoreInput): input is ScoreInput & { numerator: number; denominator: number } {
  return input.numerator !== null && input.numerator !== undefined && input.denominator !== null && input.denominator !== undefined
}

/**
 * The raw number behind a value, in the person's terms: "2 call-outs in 13 scheduled shifts",
 * "On time for 6 of 11 shifts worked", "11 of 12 charts", "1 this period" (a custom behavior counted over the period), "91%" (a summary charting figure), or a plain count for a custom behavior. Null when
 * there is nothing measured.
 */
export function describeCounted(input: ScoreInput): string | null {
  if (isCounted(input)) {
    if (input.denominator === PERCENT_OF && input.key === 'charting_completion') {
      return `${input.numerator}%`
    }
    const phrase = COUNTED_PHRASES[input.key]
    return phrase ? phrase(input.numerator, input.denominator) : `${input.numerator} of ${input.denominator}`
  }
  if (input.measure === null || input.measure === undefined) {
    return null
  }
  return input.denominator === null ? `${input.measure} this period` : `${Math.round(input.measure * 1000) / 10}%`
}
